import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/components/ui/sonner';
import { rotaService } from '../services/rotaService';
import type { SalvarRotaRequest, SalvarRotaMontagemRequest } from '../types';

// ── Combo ─────────────────────────────────────────────────────────────────
export function useRotaCombo() {
  const { data = [], isLoading } = useQuery({
    queryKey: ['rotas-combo'],
    queryFn: rotaService.getCombo,
    staleTime: 60_000,
  });
  return { rotas: data, isLoading };
}

// ── Cadastro de Rotas ─────────────────────────────────────────────────────
export function useRotas(busca?: string) {
  const queryClient = useQueryClient();

  const { data: response, isLoading } = useQuery({
    queryKey: ['rotas', busca],
    queryFn: () => rotaService.listar({ busca, tamanhoPagina: 100 }),
  });

  const invalidar = () => {
    queryClient.invalidateQueries({ queryKey: ['rotas'] });
    queryClient.invalidateQueries({ queryKey: ['rotas-combo'] });
  };

  const { mutate: criar, isPending: isCriando } = useMutation({
    mutationFn: rotaService.criar,
    onSuccess: () => { invalidar(); toast.success('Rota criada com sucesso!'); },
    onError: () => toast.error('Erro ao criar rota.'),
  });

  const { mutate: atualizar, isPending: isAtualizando } = useMutation({
    mutationFn: ({ id, req }: { id: number; req: SalvarRotaRequest }) =>
      rotaService.atualizar(id, req),
    onSuccess: () => { invalidar(); toast.success('Rota atualizada com sucesso!'); },
    onError: () => toast.error('Erro ao atualizar rota.'),
  });

  const { mutate: inativar, isPending: isInativando } = useMutation({
    mutationFn: rotaService.inativar,
    onSuccess: () => { invalidar(); toast.success('Rota inativada.'); },
    onError: () => toast.error('Erro ao inativar rota.'),
  });

  return {
    rotas: response?.itens ?? [],
    isLoading,
    criar,
    atualizar,
    inativar,
    isCriando,
    isAtualizando,
    isInativando,
  };
}

// ── Montagem de Rota ──────────────────────────────────────────────────────
export function useRotaMontagem(rotaId?: number) {
  const queryClient = useQueryClient();

  const { data: pontos = [], isLoading } = useQuery({
    queryKey: ['rota-montagem', rotaId],
    queryFn: () => rotaService.getMontagemPorRota(rotaId!),
    enabled: !!rotaId,
  });

  const { mutate: salvarMontagem, isPending: isSalvando } = useMutation({
    mutationFn: (req: SalvarRotaMontagemRequest) => rotaService.salvarMontagem(req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rota-montagem', rotaId] });
      toast.success('Montagem salva com sucesso!');
    },
    onError: () => toast.error('Erro ao salvar montagem.'),
  });

  return { pontos, isLoading, salvarMontagem, isSalvando };
}
